import React from "react";
import { Tooltip } from "antd";
import { Icon } from "@iconify/react";
import { checkPermission } from "@comps/commonFunctions";
import { useConfirm } from "./BaseConfirm";
import "./layouts-styles.css";

/**
 * Row actions.
 *
 * Grouped into one hairline-bordered cluster so a row's controls read as
 * a single unit instead of three differently-coloured link buttons at
 * three different sizes. Permission gating is unchanged.
 */
const Action = ({ label, icon, onClick, danger }) => (
  <Tooltip title={ label }>
    <button
      type="button"
      className={ `row-action ${danger ? 'is-danger' : ''}` }
      onClick={ onClick }
      aria-label={ label }
    >
      <Icon icon={ icon } width={ 18 } />
    </button>
  </Tooltip>
);

const BaseAction = (props) => {
  const { module, each, onView, onAdd, onEdit, onDelete, children, deleteConfirm } = props;
  const confirm = useConfirm();

  /**
   * Delete asks in a dialog rather than a bubble hanging off the row —
   * the bubble opened off the bottom of the table on the last row, and
   * had no room to say what was being deleted. `deleteConfirm` lets a
   * screen name the record; without it the wording stays generic.
   */
  const askThenDelete = () => confirm({
    tone: "danger",
    icon: "tabler:trash",
    title: "Delete this record?",
    confirmText: "Delete",
    cancelText: "Keep it",
    outcome: "Deleting is permanent — it cannot be undone.",
    ...(deleteConfirm ? deleteConfirm(each) : {}),
    // If the screen's handler hands back a promise the dialog waits on
    // it; if it does not, this closes exactly as it did before.
    action: () => onDelete(each),
  });

  return (
    <div className="row-actions">
      { children }

      { onView && checkPermission(module + "_view") &&
        <Action label="View" icon="tabler:eye" onClick={ () => onView(each) } /> }

      { onAdd && checkPermission(module + "_create") &&
        <Action label="Add" icon="tabler:plus" onClick={ () => onAdd(each) } /> }

      { onEdit && checkPermission(module + "_edit") &&
        <Action label="Edit" icon="tabler:pencil" onClick={ () => onEdit(each) } /> }

      { onDelete && checkPermission(module + "_delete") &&
        <Action label="Delete" icon="tabler:trash" onClick={ askThenDelete } danger /> }
    </div>
  );
};

export default BaseAction;
